// API bindings: checks (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { CheckResponse, CheckCreateRequest, CheckListResponse, CheckUpdateRequest, CheckRunResponse, CheckResultListResponse, CheckResultResponse, CheckStatisticsResponse } from "./definitions";

export class ApiChecks {
    /**
     * Method: POST
     * Path: /services/{serviceId}/checks
     * Creates a check inside a service.
     * @param serviceId Service identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateCheck(serviceId: string, body: CheckCreateRequest): RequestParams<CheckResponse, CreateCheckErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/services/${encodeURIComponent(serviceId)}/checks`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_RECOVERY_THRESHOLD", handler.badRequestInvalidRecoveryThreshold)
                    .add(400, "INVALID_FAILURE_THRESHOLD", handler.badRequestInvalidFailureThreshold)
                    .add(400, "INVALID_TIMEOUT", handler.badRequestInvalidTimeout)
                    .add(400, "INVALID_INTERVAL", handler.badRequestInvalidInterval)
                    .add(400, "INVALID_CONFIGURATION", handler.badRequestInvalidConfiguration)
                    .add(400, "INVALID_TYPE", handler.badRequestInvalidType)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /services/{serviceId}/checks
     * Lists the checks belonging to a service.
     * @param serviceId Service identifier
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListServiceChecks(serviceId: string, queryParams: ListServiceChecksQueryParameters): RequestParams<CheckListResponse, ListServiceChecksErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/services/${encodeURIComponent(serviceId)}/checks` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /checks/{id}
     * Gets a check.
     * @param id Check identifier
     * @returns The request parameters
     */
    public static GetCheck(id: string): RequestParams<CheckResponse, GetCheckErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: PUT
     * Path: /checks/{id}
     * Updates a check.
     * @param id Check identifier
     * @param body Body parameters
     * @returns The request parameters
     */
    public static UpdateCheck(id: string, body: CheckUpdateRequest): RequestParams<CheckResponse, UpdateCheckErrorHandler> {
        return {
            method: "PUT",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "NO_FIELDS", handler.badRequestNoFields)
                    .add(400, "INVALID_RECOVERY_THRESHOLD", handler.badRequestInvalidRecoveryThreshold)
                    .add(400, "INVALID_FAILURE_THRESHOLD", handler.badRequestInvalidFailureThreshold)
                    .add(400, "INVALID_TIMEOUT", handler.badRequestInvalidTimeout)
                    .add(400, "INVALID_INTERVAL", handler.badRequestInvalidInterval)
                    .add(400, "INVALID_CONFIGURATION", handler.badRequestInvalidConfiguration)
                    .add(400, "INVALID_TYPE", handler.badRequestInvalidType)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "INVALID_BODY", handler.badRequestInvalidBody)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /checks/{id}
     * Deletes a check.
     * @param id Check identifier
     * @returns The request parameters
     */
    public static DeleteCheck(id: string): RequestParams<void, DeleteCheckErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /checks/{id}/run
     * Executes a check immediately using the same flow as scheduled executions.
     * @param id Check identifier
     * @returns The request parameters
     */
    public static RunCheck(id: string): RequestParams<CheckRunResponse, RunCheckErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}/run`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /checks/{id}/results
     * Lists persisted execution results for a check, newest first.
     * @param id Check identifier
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListCheckResults(id: string, queryParams: ListCheckResultsQueryParameters): RequestParams<CheckResultListResponse, ListCheckResultsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}/results` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_RESULT_STATUS", handler.badRequestInvalidResultStatus)
                    .add(400, "INVALID_PAGINATION", handler.badRequestInvalidPagination)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /checks/{id}/results/latest
     * Gets the latest persisted execution result for a check.
     * @param id Check identifier
     * @returns The request parameters
     */
    public static GetLatestCheckResult(id: string): RequestParams<CheckResultResponse, GetLatestCheckResultErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}/results/latest`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /checks/{id}/statistics
     * Gets check execution and incident statistics for a period.
     * @param id Check identifier
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static GetCheckStatistics(id: string, queryParams: GetCheckStatisticsQueryParameters): RequestParams<CheckStatisticsResponse, GetCheckStatisticsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/checks/${encodeURIComponent(id)}/statistics` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(400, "INVALID_PERIOD", handler.badRequestInvalidPeriod)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for CreateCheck
 */
export type CreateCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_FIELDS
     */
    badRequestInvalidFields: () => void;

    /**
     * Handler for status = 400 and code = INVALID_BODY
     */
    badRequestInvalidBody: () => void;

    /**
     * Handler for status = 400 and code = INVALID_NAME
     */
    badRequestInvalidName: () => void;

    /**
     * Handler for status = 400 and code = INVALID_TYPE
     */
    badRequestInvalidType: () => void;

    /**
     * Handler for status = 400 and code = INVALID_CONFIGURATION
     */
    badRequestInvalidConfiguration: () => void;

    /**
     * Handler for status = 400 and code = INVALID_INTERVAL
     */
    badRequestInvalidInterval: () => void;

    /**
     * Handler for status = 400 and code = INVALID_TIMEOUT
     */
    badRequestInvalidTimeout: () => void;

    /**
     * Handler for status = 400 and code = INVALID_FAILURE_THRESHOLD
     */
    badRequestInvalidFailureThreshold: () => void;

    /**
     * Handler for status = 400 and code = INVALID_RECOVERY_THRESHOLD
     */
    badRequestInvalidRecoveryThreshold: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ListServiceChecks
 */
export type ListServiceChecksErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_PAGINATION
     */
    badRequestInvalidPagination: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Query parameters for ListServiceChecks
 */
export interface ListServiceChecksQueryParameters {
    /**
     * Page number, starting at 1.
     */
    page?: number;

    /**
     * Number of checks per page, maximum 100.
     */
    pageSize?: number;
}

/**
 * Error handler for GetCheck
 */
export type GetCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for UpdateCheck
 */
export type UpdateCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_FIELDS
     */
    badRequestInvalidFields: () => void;

    /**
     * Handler for status = 400 and code = INVALID_BODY
     */
    badRequestInvalidBody: () => void;

    /**
     * Handler for status = 400 and code = INVALID_NAME
     */
    badRequestInvalidName: () => void;

    /**
     * Handler for status = 400 and code = INVALID_TYPE
     */
    badRequestInvalidType: () => void;

    /**
     * Handler for status = 400 and code = INVALID_CONFIGURATION
     */
    badRequestInvalidConfiguration: () => void;

    /**
     * Handler for status = 400 and code = INVALID_INTERVAL
     */
    badRequestInvalidInterval: () => void;

    /**
     * Handler for status = 400 and code = INVALID_TIMEOUT
     */
    badRequestInvalidTimeout: () => void;

    /**
     * Handler for status = 400 and code = INVALID_FAILURE_THRESHOLD
     */
    badRequestInvalidFailureThreshold: () => void;

    /**
     * Handler for status = 400 and code = INVALID_RECOVERY_THRESHOLD
     */
    badRequestInvalidRecoveryThreshold: () => void;

    /**
     * Handler for status = 400 and code = NO_FIELDS
     */
    badRequestNoFields: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteCheck
 */
export type DeleteCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for RunCheck
 */
export type RunCheckErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ListCheckResults
 */
export type ListCheckResultsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_PAGINATION
     */
    badRequestInvalidPagination: () => void;

    /**
     * Handler for status = 400 and code = INVALID_RESULT_STATUS
     */
    badRequestInvalidResultStatus: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Query parameters for ListCheckResults
 */
export interface ListCheckResultsQueryParameters {
    /**
     * Page number, starting at 1.
     */
    page?: number;

    /**
     * Number of results per page, maximum 100.
     */
    pageSize?: number;

    /**
     * Result status - eg: SUCCESS,FAILURE
     */
    status?: "SUCCESS" | "FAILURE";
}

/**
 * Error handler for GetLatestCheckResult
 */
export type GetLatestCheckResultErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for GetCheckStatistics
 */
export type GetCheckStatisticsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Handler for status = 400 and code = INVALID_PERIOD
     */
    badRequestInvalidPeriod: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Query parameters for GetCheckStatistics
 */
export interface GetCheckStatisticsQueryParameters {
    /**
     * Statistics period: 24h, 7d or 30d.
     */
    period?: string;
}
